import React, {useEffect, useState} from 'react';
import ReactDOMServer from 'react-dom/server';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/browser';
import {updateClientDetails} from 'store';
import {toast} from 'react-toastify';
import {Step1} from 'views/Calculator/step1';
import {Step2} from 'views/Calculator/step2';
import {Step3} from 'views/Calculator/step3';
import Header from 'components/Header';
import Pdf from 'views/Pdf';
import {pdf} from '@react-pdf/renderer';
import {useStateMachine} from 'little-state-machine';
import {sendEmail} from 'api';
import ResetDialog from 'components/ResetDialog';
import {Box} from '@chakra-ui/core';
import {Pie, PieChart} from 'recharts';
import {useCurrentPng} from 'recharts-to-png';
import products from './constants/products';
import OpenPdfModal from './views/Pdf/OpenPdfModal';
import {LEAD_MAIL, LOGO_FILENAME} from 'config';
import {blobToBase64, getBase64Data} from 'utils/data';
import EmailClient, {
	getPlainTextEmail as getClientPlainTextEmail,
} from 'components/EmailClient';
import EmailLead, {
	getPlainTextEmail as getLeadPlainTextEmail,
} from 'components/EmailLead';
import logo from 'assets/logo';

const sortMachinesByWeight = (data) => {
	return data.sort((a, b) =>
		Number(a['excavatorOperatingWeightMax(t)']) >
		Number(b['excavatorOperatingWeightMax(t)'])
			? 1
			: -1,
	);
};

const emptyUser = {
	name: '',
	email: '',
	mobile: '',
};

const pngOpts = {backgroundColor: 'transparent'};

const App = () => {
	const [recommendedSleipner, setRecommendedSleipner] = useState(null);
	const [currentView, setCurrentView] = useState(1);
	const [weight, setWeight] = useState(null);
	const [saving, setSaving] = useState(false);
	const {state, actions} = useStateMachine({updateClientDetails});
	const [showResetDialog, setShowResetDialog] = useState(false);
	const sortedMachines = sortMachinesByWeight(products);
	const [chartUtilizationWithout, setChartUtilizationWithout] = useState(null);
	const [chartUtilizationWithSleipnerSavings, setChartUtilizationWithSleipnerSavings] =
		useState(null);
	const [chartUtilizationWithSleipner, setChartUtilizationWithSleipner] = useState(null);
	const [pdfUrl, setPdfUrl] = useState(null);

	const [getChartUtilizationWithout, {ref: chartUtilizationWithoutRef}] =
		useCurrentPng(pngOpts);
	const [
		getChartUtilizationWithSleipnerSavings,
		{ref: chartUtilizationWithSleipnerSavingsRef},
	] = useCurrentPng(pngOpts);
	const [getChartUtilizationWithSleipner, {ref: chartUtilizationWithSleipnerRef}] =
		useCurrentPng(pngOpts);

	const [emailSentAddress, setEmailSentAddress] = useState('');

	useEffect(() => {
		actions.updateClientDetails({});
	}, []);

	useEffect(() => {
		if (!weight) return;

		// Pick recommended one based on excavator weight
		const recommended = sortedMachines.filter(
			(s) => Number(s['excavatorOperatingWeightMax(t)']) >= Number(weight),
		)[0];

		setRecommendedSleipner(recommended);
	}, [weight]);

	const createPdf = async (download = true) => {
		try {
			setSaving(true);

			const {form, calculatedValues, clientDetails} = state;

			const chartUtilizationWithoutUrl = await getChartUtilizationWithout();
			const chartUtilizationWithSleipnerSavingsUrl =
				await getChartUtilizationWithSleipnerSavings();
			const chartUtilizationWithSleipnerUrl = await getChartUtilizationWithSleipner();

			const {clientFirstName, clientLastName, clientCompany, clientEmail} = clientDetails;
			const customer = {
				firstName: clientFirstName ?? '',
				lastName: clientLastName ?? '',
				company: clientCompany ?? '',
				email: clientEmail ?? '',
			};

			try {
				const pdfBlob = await pdf(
					<Pdf
						formValues={form}
						calculatedValues={calculatedValues}
						recommendedSleipner={recommendedSleipner}
						showellUser={emptyUser}
						customer={customer}
						charts={{
							chartUtilizationWithout: chartUtilizationWithoutUrl,
							chartUtilizationWithSleipnerSavings: chartUtilizationWithSleipnerSavingsUrl,
							chartUtilizationWithSleipner: chartUtilizationWithSleipnerUrl,
						}}
					/>,
				).toBlob();

				await sendEmails(pdfBlob);

				if (download) {
					const link = document.createElement('a');
					link.href = window.URL.createObjectURL(pdfBlob);
					link.download = 'Sleipner E Series benefit calculator report.pdf';
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					// Open PDF preview in browser
					const fileURL = window.URL.createObjectURL(pdfBlob);
					const pdfWindow = window.open(fileURL, '_blank');
					if (pdfWindow === null) {
						// async window.open() will get caught Safari popup blocker
						// => open modal where new window can be opened from user action
						setPdfUrl(fileURL);
					}
				}
			} catch (err) {
				Sentry.captureException(err);
				toast.error('PDF:n luonti epäonnistui');
			} finally {
				setSaving(false);
			}
		} catch (err) {
			Sentry.captureException(err);
			const msg = err?.message ? err.message : 'Tallennus epäonnistui';
			setSaving(false);
			toast.error(msg);
		}
	};

	const sendEmails = async (pdfBlob) => {
		const {clientDetails} = state;

		// Send emails only if client entered email
		// Don't send again if already sent to same address
		if (!clientDetails.clientEmail || clientDetails.clientEmail === emailSentAddress) {
			return;
		}

		const emails = [];
		const attachments = [];
		const inlines = [
			{
				filename: LOGO_FILENAME,
				fileblob: getBase64Data(logo),
				mimetype: 'image/jpeg',
			},
		];

		const base64 = await blobToBase64(pdfBlob);
		if (base64) {
			attachments.push({
				filename: 'Sleipner E Series benefit calculator report.pdf',
				fileblob: getBase64Data(base64),
				mimetype: 'application/pdf',
			});
		}

		if (attachments.length) {
			// Client email
			const htmlClient = ReactDOMServer.renderToStaticMarkup(
				<EmailClient {...clientDetails} />,
			);
			emails.push({
				subject: 'Sleipner E Series benefit calculator report',
				html: htmlClient,
				plainText: getClientPlainTextEmail(),
				recipients: [clientDetails.clientEmail],
				attachments,
				inlines,
			});

			// Lead email
			const leadRecipients = [LEAD_MAIL];
			const htmlLead = ReactDOMServer.renderToStaticMarkup(
				<EmailLead {...clientDetails} />,
			);
			emails.push({
				subject: 'New lead - E Series benefit calculator',
				html: htmlLead,
				plainText: getLeadPlainTextEmail(),
				recipients: leadRecipients,
				attachments,
			});
		}

		await Promise.all(emails.map(sendEmail));

		setEmailSentAddress(clientDetails.clientEmail);
	};

	const resetForm = () => {
		setShowResetDialog(true);
	};

	const setDataForCharts = ({recommendedSleipner, calculatedValues}) => {
		const chartUtilizationWithoutData = [
			{
				value: calculatedValues['Annual time spent travelling without Sleipner (%)'],
				fill: '#000',
			},
			{
				value:
					100 - calculatedValues['Annual time spent travelling without Sleipner (%)'],
				fill: '#666',
			},
		];
		const chartUtilizationWithSleipnerSavingsData = [
			{
				value: calculatedValues['Annual time spent travelling with Sleipner (%)'],
				fill: '#000',
			},
			{
				value: 100 - calculatedValues['Annual time spent travelling with Sleipner (%)'],
				fill: '#666',
			},
			{
				value:
					calculatedValues['Annual time spent travelling without Sleipner (%)'] -
					calculatedValues['Annual time spent travelling with Sleipner (%)'],
				fill: '#fff',
			},
		];
		const chartUtilizationWithSleipnerData = [
			{
				value: calculatedValues['Annual time spent travelling with Sleipner (%)'],
				fill: '#000',
			},
			{
				value: 100 - calculatedValues['Annual time spent travelling with Sleipner (%)'],
				fill: '#666',
			},
		];

		setChartUtilizationWithout(chartUtilizationWithoutData);
		setChartUtilizationWithSleipnerSavings(chartUtilizationWithSleipnerSavingsData);
		setChartUtilizationWithSleipner(chartUtilizationWithSleipnerData);
	};

	return (
		<>
			<Header resetForm={resetForm} />

			<Box mx={4} pb={8}>
				{/* Excavator weight information */}
				{currentView === 1 && (
					<>
						<Step1 setWeight={setWeight} setCurrentView={setCurrentView} />
					</>
				)}

				{/* Additional details input */}
				{currentView === 2 && (
					<Step2
						recommendedSleipner={recommendedSleipner}
						setDataForCharts={setDataForCharts}
						setCurrentView={setCurrentView}
					/>
				)}

				{/* Summary page */}
				{currentView === 3 && (
					<Step3
						createPdf={createPdf}
						setCurrentView={setCurrentView}
						recommendedSleipner={recommendedSleipner}
						loading={saving}
					/>
				)}

				{showResetDialog && (
					<ResetDialog
						isOpen={showResetDialog}
						onConfirm={() => {
							setCurrentView(1);
							setShowResetDialog(false);
						}}
						onClose={() => setShowResetDialog(false)}
					/>
				)}

				{!!pdfUrl && (
					<OpenPdfModal pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} isOpen />
				)}

				<Box opacity={0} position="absolute" top={0} left={0} pointerEvents="none">
					<PieChart width={110} height={110} ref={chartUtilizationWithoutRef}>
						<Pie
							animationDuration={0}
							blendStroke
							data={chartUtilizationWithout}
							dataKey="value"
							nameKey="name"
							cx="50%"
							cy="50%"
							outerRadius={50}
						/>
					</PieChart>
					<PieChart width={110} height={110} ref={chartUtilizationWithSleipnerSavingsRef}>
						<Pie
							animationDuration={0}
							blendStroke
							data={chartUtilizationWithSleipnerSavings}
							dataKey="value"
							nameKey="name"
							cx="50%"
							cy="50%"
							outerRadius={50}
						/>
					</PieChart>
					<PieChart width={110} height={110} ref={chartUtilizationWithSleipnerRef}>
						<Pie
							animationDuration={0}
							blendStroke
							data={chartUtilizationWithSleipner}
							dataKey="value"
							nameKey="name"
							cx="50%"
							cy="50%"
							outerRadius={50}
						/>
					</PieChart>
				</Box>
			</Box>
		</>
	);
};

export default App;
